import { formatToDollar } from "@inrev/common";

const SIGNIFICANT_DIGITS = 3;
const SUFFIXES = ["", "K", "M", "B", "T"] as const;

/** An abbreviated metric value plus the full dollar amount to surface in a hover popover. */
export type MetricCardCurrency = {
	/** Abbreviated to {@link SIGNIFICANT_DIGITS} significant digits, e.g. `"$557M"`, `"$10.5M"`, `"$555K"`. */
	display: string;
	/** The full, unabbreviated amount, e.g. `"$556,600,000"`. */
	full: string;
};

const roundToSignificantDigits = (scaled: number): number => {
	const integerDigits = Math.floor(Math.log10(scaled)) + 1;
	const decimals = Math.max(0, SIGNIFICANT_DIGITS - integerDigits);
	return Number(scaled.toFixed(decimals));
};

const abbreviate = (value: number): string => {
	const sign = value < 0 ? "-" : "";
	let scaled = Math.abs(value);

	if (scaled < 1_000) {
		return `${sign}$${Math.round(scaled).toLocaleString("en-US")}`;
	}

	let tier = 0;
	while (scaled >= 1_000 && tier < SUFFIXES.length - 1) {
		scaled /= 1_000;
		tier += 1;
	}

	let rounded = roundToSignificantDigits(scaled);
	// Rounding can tip a value into the next tier (e.g. 999.6K rounds to 1,000K -> 1M).
	if (rounded >= 1_000 && tier < SUFFIXES.length - 1) {
		scaled /= 1_000;
		tier += 1;
		rounded = roundToSignificantDigits(scaled);
	}

	return `${sign}$${rounded}${SUFFIXES[tier] ?? ""}`;
};

/**
 * Formats a currency value for a metric card: an abbreviated `display` limited to three
 * significant digits, and the `full` amount for the hover popover.
 */
export const formatMetricCardCurrency = (value: number): MetricCardCurrency => {
	const full = formatToDollar(value);
	return {
		display: abbreviate(value),
		full: typeof full === "string" ? full : `${value < 0 ? "-" : ""}$${Math.abs(value)}`,
	};
};
